// src/portal-app/tabs/data/useTeamData.js
//
// Data hook for the Team tab landing (Task 5). Isolates every network call
// behind the shared { status, data, reload } envelope.
//
// Field provenance:
//   - dashboardService.getStats() (PRIMARY) supplies onShiftNow, onShift and
//     scheduledToday — all derived from `stats.shifts`, which the backend
//     already date-filters to today (portal_dashboard.py `_build_shifts`).
//     onShiftNow is DELIBERATELY `stats.shifts.filter(s => s.is_clocked_in)
//     .length`, never `stats.kpis.on_shift.clocked_in` — that KPI counts
//     every shift tenant-wide with status "In Progress" with NO date filter
//     at all, so it silently over-counts on any day that isn't the KPI's
//     own notion of "today".
//   - payrunService.list() (SECONDARY) supplies payRunsDue from `.to_run`,
//     which the backend already returns urgency-sorted — never re-sorted
//     here.
//   - shiftService.getShiftsByDateRange(monday, sunday) (SECONDARY) is ONE
//     request reused for BOTH hoursThisWeek (sum of actual_hours ??
//     expected_hours — there is no tenant-wide hours endpoint) and
//     conflicts (findConflicts() over the same week's shifts, so Team tab
//     and ScheduleScreen can never disagree about an overlap).
//   - employeeService.getAllEmployees() (SECONDARY) supplies
//     activeEmployees, counting `status === 'Active'` the same way the web
//     PortalEmployeesPage does.
//
// Every call — primary and secondary alike — is fired in ONE
// Promise.allSettled batch. Only the primary's rejection sets
// status: 'error'; a failed secondary degrades only the field(s) it feeds
// (counts → null, lists → []) rather than blanking the tab.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    dashboardService,
    payrunService,
    shiftService,
    employeeService,
} from '../../../services/api';
import { rangeForPreset, toLocalISO } from '../../nav/dateRange';
import { findConflicts } from './scheduleConflicts';

export function useTeamData() {
    const [status, setStatus] = useState('loading');
    const [data, setData] = useState(null);
    const seqRef = useRef(0);

    const load = useCallback(() => {
        seqRef.current += 1;
        const seq = seqRef.current;
        setStatus('loading');

        const week = rangeForPreset('week');
        const mondayIso = toLocalISO(week.start);
        const sundayIso = toLocalISO(week.end);

        const calls = [
            { key: 'stats', promise: dashboardService.getStats() },
            { key: 'payruns', promise: payrunService.list() },
            { key: 'weekShifts', promise: shiftService.getShiftsByDateRange(mondayIso, sundayIso) },
            { key: 'employees', promise: employeeService.getAllEmployees() },
        ];

        Promise.allSettled(calls.map((c) => c.promise)).then((results) => {
            if (seq !== seqRef.current) return; // stale — a faster reload already won

            const byKey = {};
            calls.forEach((c, i) => { byKey[c.key] = results[i]; });

            const statsResult = byKey.stats;
            if (!statsResult || statsResult.status !== 'fulfilled') {
                setData(null);
                setStatus('error');
                return;
            }
            const stats = statsResult.value || {};
            const todaysShifts = Array.isArray(stats.shifts) ? stats.shifts : [];
            const onShiftShifts = todaysShifts.filter((s) => s.is_clocked_in);

            const payrunsResult = byKey.payruns;
            const payRunsDue = (payrunsResult && payrunsResult.status === 'fulfilled')
                ? ((payrunsResult.value && payrunsResult.value.to_run) || []).map((r) => ({
                    id: r.pay_run_id,
                    urgency: r.urgency,
                    totalOwed: Number(r.total_owed) || 0,
                    payDate: r.pay_date,
                }))
                : [];

            const weekShiftsResult = byKey.weekShifts;
            const weekShiftsOk = !!(weekShiftsResult && weekShiftsResult.status === 'fulfilled');
            const weekShifts = (weekShiftsOk && Array.isArray(weekShiftsResult.value))
                ? weekShiftsResult.value
                : [];
            const hoursThisWeek = weekShiftsOk
                ? weekShifts.reduce((sum, s) => sum + (Number(s.actual_hours ?? s.expected_hours) || 0), 0)
                : null;
            const conflicts = weekShiftsOk
                ? findConflicts(weekShifts).map((c) => ({ employeeName: c.employeeName, date: c.date }))
                : [];

            const employeesResult = byKey.employees;
            const activeEmployees = (employeesResult && employeesResult.status === 'fulfilled'
                && Array.isArray(employeesResult.value))
                ? employeesResult.value.filter((e) => String(e.status || '').toLowerCase() === 'active').length
                : null;

            setData({
                onShiftNow: onShiftShifts.length,
                onShift: onShiftShifts.map((s, i) => ({
                    id: i,
                    initials: s.employee_initials,
                    name: s.employee_name,
                    station: s.station,
                })),
                scheduledToday: todaysShifts.length,
                activeEmployees,
                hoursThisWeek,
                payRunsDue,
                conflicts,
            });
            setStatus('ready');
        });
    }, []);

    useEffect(() => { load(); }, [load]);

    return { status, data, reload: load };
}

export default useTeamData;
